/**
 * Invoice-list store — FR-3 failed-invoice pages for the selected deployment
 * + period (SIH-3). Cursor-paginated (R9); sort/filter state survives page
 * changes because query inputs live in this store, not in row state.
 *
 * Refresh honesty rule: a same-scope same-period reload (including cursor
 * page fetches) keeps the previous page visible under the 'refreshing'
 * marker; a scope or period change shows 'loading' — old-scope rows never
 * read as current (ux-principles §3).
 */

import { create } from 'zustand'
import { fetchInvoices } from '@api/dashboardApi'
import { ApiError, type InvoicePage, type SyncStatus, type ViewState } from '@domain/types'

interface InvoiceListScope {
  readonly tinBranch: string
  readonly from: string
  readonly to: string
  readonly status: SyncStatus
}

interface InvoiceListState {
  readonly page: ViewState<InvoicePage>
  /** The status this list filters by. Defaults to FAILED for S3; callers
   *  can opt into PENDING for the S4 Pending list (proposal §3.10). */
  readonly status: SyncStatus
  /** Scope of the page currently on screen (refresh-vs-load decision). */
  readonly scope: InvoiceListScope | null
  readonly load: (
    tinBranch: string,
    period: { from: string; to: string },
    options?: { cursor?: string; status?: SyncStatus },
    signal?: AbortSignal,
  ) => Promise<void>
}

function scopeMatches(
  scope: InvoiceListScope | null,
  tinBranch: string,
  period: { from: string; to: string },
  status: SyncStatus,
): boolean {
  return (
    scope !== null &&
    scope.tinBranch === tinBranch &&
    scope.from === period.from &&
    scope.to === period.to &&
    scope.status === status
  )
}

export const useInvoiceListStore = create<InvoiceListState>((set, get) => ({
  page: { kind: 'idle' },
  status: 'FAILED',
  scope: null,

  load: async (tinBranch, period, options, signal) => {
    const effectiveStatus: SyncStatus = options?.status ?? get().status
    const { page, scope } = get()
    const previous = page.kind === 'ready' ? page.data : page.kind === 'refreshing' ? page.previous : null
    if (previous !== null && scopeMatches(scope, tinBranch, period, effectiveStatus)) {
      set({ page: { kind: 'refreshing', previous }, status: effectiveStatus })
    } else {
      set({ page: { kind: 'loading' }, status: effectiveStatus })
    }
    try {
      const result = await fetchInvoices(
        tinBranch,
        {
          status: effectiveStatus,
          from: period.from,
          to: period.to,
          cursor: options?.cursor,
          limit: 200,
        },
        signal,
      )
      set({
        page: { kind: 'ready', data: result },
        scope: { tinBranch, from: period.from, to: period.to, status: effectiveStatus },
        status: effectiveStatus,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      const message = error instanceof ApiError ? error.message : `Cannot reach the dashboard service — ${(error as Error).message}`
      set({ page: { kind: 'error', message } })
    }
  },
}))
